import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import SectionBadge from "@/components/site/SectionBadge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { imageForPath } from "@/data/images";
import { track } from "@/lib/analytics";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

// Keep expanded modules easy on the eye — at most two sentences on screen.
const clampSentences = (t, n = 2) => {
  if (!t) return t;
  const parts = t.match(/[^.]+\.(\s|$)/g);
  if (!parts || parts.length <= n) return t;
  return parts.slice(0, n).join(" ").trim();
};

/**
 * Expandable service explorer — progressive disclosure for a service catalogue.
 * Collapsed: number, name, one-line outcome. Expanded: relevant visual,
 * short description, coverage list, deliverable chips, optional deep-link.
 * Accordion content is force-mounted (CSS-hidden when closed) so it remains
 * present in prerendered HTML for crawlers.
 * items: [{ name, outcome, body?, image?, covers?, deliverables?, link? { to, label } }]
 */
export default function ServiceExplorer({
  testid,
  badge,
  headline,
  intro,
  items,
  deliverablesLabel,
  deliverables,
  tone = "alt",
}) {
  const dark = tone === "dark";
  return (
    <section
      data-testid={testid}
      className={`py-16 md:py-20 ${dark ? "bg-[#050505] text-white" : tone === "alt" ? "bg-[#F2F2F2]" : "bg-white"}`}
    >
      <div className={GRID}>
        <SectionBadge label={badge} tone={dark ? "light" : undefined} />
        <div className="mt-6 grid grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-12">
          <div className="col-span-12 md:col-span-6">
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] max-w-2xl">
              {headline}
            </h2>
          </div>
          {intro && (
            <div className="col-span-12 md:col-span-6 md:flex md:flex-col md:justify-end">
              <p className={`text-base leading-relaxed max-w-lg ${dark ? "text-white/70" : "text-zinc-600"}`}>
                {intro}
              </p>
            </div>
          )}
        </div>

        <Accordion type="single" collapsible className={`w-full border-t reveal stagger-reveal ${dark ? "border-white/15" : "border-black/10"}`}>
          {items.map((item, i) => {
            const itemImage = item.image || (item.link && imageForPath(item.link.to));
            const hasCovers = item.covers?.length > 0;
            const bodyCol = itemImage
              ? hasCovers ? "md:col-span-4" : "md:col-span-8"
              : hasCovers ? "md:col-span-6" : "md:col-span-12";
            return (
              <AccordionItem
                key={item.name}
                value={`item-${i}`}
                className={dark ? "border-b border-white/15" : "border-b border-black/10"}
                data-testid={`${testid}-item-${i}`}
              >
                <AccordionTrigger className="text-left hover:no-underline py-6 group">
                  <span className="flex flex-1 flex-col md:flex-row md:items-center gap-2 md:gap-8 pr-4">
                    <span className="flex items-center gap-5 md:w-[45%] shrink-0">
                      <span className={`flex h-9 w-9 shrink-0 items-center justify-center text-[12px] font-semibold tracking-wider transition-colors duration-300 ${dark ? "bg-white text-black" : "bg-black text-white"} group-data-[state=open]:bg-zinc-700 group-data-[state=open]:text-white`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className={`font-serif-display text-xl md:text-2xl leading-tight ${dark ? "text-white" : "text-black"}`}>
                        {item.name}
                      </span>
                    </span>
                    <span className={`text-sm leading-relaxed ${dark ? "text-white/60" : "text-zinc-500"}`}>
                      {item.outcome}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent
                  forceMount
                  className="data-[state=closed]:hidden overflow-visible"
                >
                  <div className="pb-8 md:pl-14 grid grid-cols-12 gap-6">
                    {itemImage && (
                      <div className="col-span-12 md:col-span-4">
                        <div className="aspect-[4/3] overflow-hidden bg-zinc-200">
                          <img
                            src={itemImage}
                            alt={item.name}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    )}
                    <div className={`col-span-12 ${bodyCol}`}>
                      {item.body && (
                        <p className={`text-[15px] leading-relaxed ${dark ? "text-white/75" : "text-zinc-700"}`}>
                          {clampSentences(item.body)}
                        </p>
                      )}
                      {item.link && (
                        <Link
                          to={item.link.to}
                          data-testid={`${testid}-link-${i}`}
                          onClick={() => track("explorer_link_open", { item: item.name })}
                          className={`group/link mt-5 inline-flex items-center gap-2 text-sm font-medium ${dark ? "text-white" : "text-black"}`}
                        >
                          {item.link.label}
                          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                        </Link>
                      )}
                    </div>
                    {hasCovers && (
                      <div className={`col-span-12 ${itemImage ? "md:col-span-4" : "md:col-span-6"}`}>
                        <div className={`text-[11px] font-semibold tracking-[0.24em] uppercase ${dark ? "text-white/50" : "text-zinc-500"}`}>
                          What it covers
                        </div>
                        <ul className="mt-3 grid grid-cols-1 gap-y-2">
                          {item.covers.map((c) => (
                            <li key={c} className={`flex items-start gap-2.5 text-sm leading-relaxed ${dark ? "text-white/80" : "text-zinc-800"}`}>
                              <Check className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
                              {c}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {item.deliverables?.length > 0 && (
                      <div className="col-span-12">
                        <div className={`text-[11px] font-semibold tracking-[0.24em] uppercase ${dark ? "text-white/50" : "text-zinc-500"}`}>
                          Typical deliverables
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {item.deliverables.map((d) => (
                            <span
                              key={d}
                              className={`inline-flex items-center min-h-8 px-3 py-1.5 border text-xs leading-snug ${dark ? "border-white/20 text-white/80" : "border-black/10 text-zinc-800 bg-white"}`}
                            >
                              {d}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        {deliverables?.length > 0 && (
          <div className="mt-10" data-testid={`${testid}-deliverables`}>
            <div className={`text-[11px] font-semibold tracking-[0.24em] uppercase ${dark ? "text-white/50" : "text-zinc-500"}`}>
              {deliverablesLabel || "What this can include"}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {deliverables.map((d) => (
                <span
                  key={d}
                  className={`inline-flex items-center min-h-8 px-3 py-1.5 border text-xs leading-snug ${dark ? "border-white/20 text-white/80" : "border-black/15 text-zinc-700 bg-white/70"}`}
                >
                  {d}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
